/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import { useActor } from '@xstate/react'
import { AnimatePresence, Variants, motion } from 'framer-motion'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { rpMediaQueries } from '../../core/theme'
import { BoxStore } from '../../stores/RussianPoker/Box.store'
import {
  DECISION_BUTTONS_MIN_TOTAL_HEIGHT,
  DETAIL_ROW_WIDTH,
} from '../layout/rpDecisionLayout'
import DecisionBtn from '../DecisionBtn'
import { Typography } from '../Typography'

const Container = styled.div`
  /* Keep decision controls aligned with 6-card detail row width. */
  --decision-min-width: ${DETAIL_ROW_WIDTH.sixCards.mobile}px;
  padding: 8px 0 4px;
  width: 100%;
  min-width: var(--decision-min-width);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  min-height: ${DECISION_BUTTONS_MIN_TOTAL_HEIGHT.mobile}px;

  ${rpMediaQueries.tablet} {
    --decision-min-width: ${DETAIL_ROW_WIDTH.sixCards.tablet}px;
    padding: 8px 0 4px;
    min-height: ${DECISION_BUTTONS_MIN_TOTAL_HEIGHT.tablet}px;
  }

  ${rpMediaQueries.desktop} {
    --decision-min-width: ${DETAIL_ROW_WIDTH.sixCards.desktop}px;
    min-height: ${DECISION_BUTTONS_MIN_TOTAL_HEIGHT.desktop}px;
  }
`

const PanelBase = styled(motion.div)`
  grid-row: 1;
  grid-column: 1;
  width: 100%;
  display: flex;
  gap: 8px;

  ${rpMediaQueries.tablet} {
    gap: 12px;
  }

  ${rpMediaQueries.desktop} {
    gap: 16px;
  }

  & > button {
    flex: 1 0 0;
    min-width: 0;
  }
`

const ExtraContent = styled(PanelBase)`
  align-items: center;
  justify-content: space-evenly;
`

const panelVariants: Variants = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.2, ease: 'easeOut' } },
  exit: { opacity: 0, y: -6, transition: { duration: 0.15, ease: 'easeIn' } },
}

type DecisionButtonsProps = {
  box: BoxStore
}

const DecisionButtons: FC<DecisionButtonsProps> = observer(({ box }) => {
  const { t } = useTranslation()
  const {
    sendSwitchCards,
    sendInsurance,
    sendBuySixCard,
    sendPass,
    sendBet,
    ui,
    seatNumber,
  } = box

  const [state] = useActor(box.ui.service)
  const { cardsForSwitch } = state.context

  const handleConfirmBuySixCard = () => {
    ui.sendConfirm()
    sendBuySixCard()
  }
  const handleCancelBuySixCard = () => ui.sendCancel()

  const handleConfirmSwitch = () => {
    if (!cardsForSwitch.length) return
    ui.sendConfirm()
    sendSwitchCards(cardsForSwitch)
  }
  const handleCancelSwitch = () => ui.sendCancel()

  const handleCancelInsurance = () => ui.sendCancel()

  const handleConfirmInsurance = () => {
    ui.sendConfirm()
    sendInsurance(ui.insuranceTemp)
  }

  return (
    <Container>
      <AnimatePresence mode="wait">
        {state.matches('idle') && (
          <PanelBase key="idle" {...panelVariants}>
            <DecisionBtn
              data-cy={`pass-btn-${seatNumber}`}
              text={t('pass')}
              variant="reject"
              onClick={sendPass}
            />
            <DecisionBtn
              data-cy={`bet-btn-${seatNumber}`}
              text={t('bet')}
              variant="accept"
              onClick={sendBet}
            />
          </PanelBase>
        )}

        {state.matches('switch') && (
          <ExtraContent key="switch" {...panelVariants}>
            <DecisionBtn
              text={t('cancel')}
              data-cy={`cancel-switch-btn-${seatNumber}`}
              variant="reject"
              onClick={handleCancelSwitch}
            />
            <DecisionBtn
              text={t('switch')}
              data-cy={`confirm-switch-btn-${seatNumber}`}
              variant="accept"
              onClick={handleConfirmSwitch}
            />
          </ExtraContent>
        )}

        {state.matches('insurance') && (
          <ExtraContent key="insurance" {...panelVariants}>
            <DecisionBtn
              text={t('cancel')}
              data-cy={`cancel-insurance-btn-${seatNumber}`}
              variant="reject"
              onClick={handleCancelInsurance}
            />
            <DecisionBtn
              text={t('insure')}
              data-cy={`confirm-insurance-btn-${seatNumber}`}
              variant="accept"
              onClick={handleConfirmInsurance}
            />
          </ExtraContent>
        )}

        {state.matches('sixCard') && (
          <ExtraContent key="sixCard" {...panelVariants}>
            <DecisionBtn
              text={t('cancel')}
              data-cy={`cancel-buy-six-card-btn-${seatNumber}`}
              variant="reject"
              onClick={handleCancelBuySixCard}
            />
            <DecisionBtn
              text={t('buySixCard')}
              data-cy={`confirm-buy-six-card-btn-${seatNumber}`}
              variant="accept"
              onClick={handleConfirmBuySixCard}
            />
          </ExtraContent>
        )}

        {state.matches('submitting') && (
          <ExtraContent
            key="submitting"
            {...panelVariants}
            data-cy={`submitting-layout-${seatNumber}`}
            css={{ justifyContent: 'center', pointerEvents: 'none' }}>
            <Typography
              variant="head2"
              color="white"
              css={{ opacity: 0.5, textAlign: 'center' }}>
              ...
            </Typography>
          </ExtraContent>
        )}

        {state.matches('pendingCards') && (
          <ExtraContent
            key="pendingCards"
            {...panelVariants}
            data-cy={`pending-cards-layout-${seatNumber}`}
            css={{ justifyContent: 'center' }}>
            <Typography
              variant="head2"
              color="white"
              css={{ opacity: 0.5, textAlign: 'center' }}>
              {t('waitCards')}...
            </Typography>
          </ExtraContent>
        )}
      </AnimatePresence>
    </Container>
  )
})

export default DecisionButtons
